import React, { Suspense, useEffect, useRef, useCallback, useState } from 'react';
import { BrowserRouter, useRoutes, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import useFirebaseNotifications from './hooks/useFirebaseNotifications';
import { useInitializeAppData } from './hooks/useInitializeAppData';
import './App.css';
import './components/common/CommonText.css';
import './components/common/Table.css';
import './components/common/CommonPage.css';
import './pages/Login/Registration.css';
import './pages/Login/common_text.css';
import './pages/Sales/SalesOrder/sales.css'
import FeedbackPopup from './components/popup/FeedbackPopUp/Feedback';
import useFeedbackPopupStore from "./store/useFeedbackPopupStore";


import Spinner from './components/common/Spinner';
import { createRoutes } from './Routes';
import NewInSalesroute from './components/popup/NewInSalesroute/NewInSalesroute';
import { markReleaseAsSeen } from './services/CommonApi';
import useReleasePopupStore from './store/useReleasePopupStore';
import { useLocation } from 'react-router-dom';
import PlanExpiredPopup from './components/popup/PlanRenewalPopup/RenewalPopUp';
import usePlanExpiredPopupStore from './store/Useplanexpiredpopupstore';
import { getCurrentPlan, requestPlanExtension } from './services/Planexpiredapi';
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import ChatWidget from './components/ChatWidget/ChatWidget';
import useFeatureStore from './store/useFeatureStore';

const EXCLUDE_PATHS = new Set([
  '/',
  '/login',
  '/registration',
  '/home',
  '/plans',
  '/plan-configure',
  '/plan-costomize',
  '/payment/stripe/success',
  '/payment/stripe/cancel',
  '/password-modification',
]);

// ─────────────────────────────────────────────────────────────────────────────

function AppRouter() {
  useInitializeAppData();

  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isExcludedPath = EXCLUDE_PATHS.has(location.pathname);
  const isChatBotEnabled = useFeatureStore((state) => state.isFeatureEnabled("chat_bot"));

  // ── Release popup ─────────────────────────────────────────────────────────
  const { showPopup: showRelease, release, clearReleasePopup } = useReleasePopupStore();

  const handleReleaseClose = useCallback(async () => {
    if (release?.id) await markReleaseAsSeen(release.id);
    clearReleasePopup();
  }, [release?.id, clearReleasePopup]);

  // ── Plan expired popup ────────────────────────────────────────────────────
  const {
    showPopup: showPlanExpired,
    isFreePlan,
    canExtend,
    extensionRequested,
    setPlanExpiredPopup,
    clearPlanExpiredPopup,
  } = usePlanExpiredPopupStore();

  const planCheckInFlight = useRef(false);
  const checkedPaths = useRef(new Set());
  const [isCheckingPlan, setIsCheckingPlan] = useState(true);

  // Reset cached state on every fresh login (including account switches).
  // Without this, checkedPaths from the previous session causes the plan
  // check to short-circuit and leaves stale showPopup:true visible.
  useEffect(() => {
    if (isAuthenticated) {
      checkedPaths.current = new Set();
      planCheckInFlight.current = false;
      setIsCheckingPlan(true);
      clearPlanExpiredPopup();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Immediately clear the popup whenever user is on an excluded path
  // (covers the /plan-configure redirect case — no stale popup from a previous session)
  useEffect(() => {
    if (isExcludedPath && showPlanExpired) {
      clearPlanExpiredPopup();
    }
  }, [isExcludedPath, showPlanExpired, clearPlanExpiredPopup]);

  useEffect(() => {
    // Don't check on unauthenticated or excluded pages
    if (!isAuthenticated || isExcludedPath) {
      setIsCheckingPlan(false);
      return;
    }

    if (checkedPaths.current.has(location.pathname)) {
      setIsCheckingPlan(false);
      return;
    }

    if (planCheckInFlight.current) return;

    planCheckInFlight.current = true;
    setIsCheckingPlan(true);

    (async () => {
      try {
        const res = await getCurrentPlan();

        if (res?.has_pending_subscription) {
          clearPlanExpiredPopup();
          navigate('/plan-configure', {
            state: {
              planId: res.pending_plan.plan_id,
              billingCycle: res.pending_plan.billing_cycle,
              currentPlan: {
                user_limit: res.pending_plan.user_limit,
                maps_calls_limit: res.pending_plan.maps_calls_limit,
                ai_credits_limit: res.pending_plan.ai_credits_limit
              }
            }
          });
          return;
        }

        if (res?.show_popup) {
          const planData = res.data || {};
          const planType = planData.plan_type;

          // isFreePlan controls the general 'vibe' (text/colors)
          const isFree = planType === 'free' || planType === 'trial' || planType === null;

          // canUserExtend MUST be explicit - only if the last expired plan was free/trial
          // and they haven't already had an approved extension
          const canUserExtend = (planType === 'free' || planType === 'trial') && !planData.extension_approved;

          const extRequested = planData.extension_requested ?? false;

          setPlanExpiredPopup({
            showPopup: true,
            isFreePlan: isFree,
            canExtend: canUserExtend,
            extensionRequested: extRequested,
          });
        } else {
          checkedPaths.current.add(location.pathname);
          if (showPlanExpired) clearPlanExpiredPopup();
        }
      } catch (err) {
        console.error('Failed to check current plan:', err);
      } finally {
        planCheckInFlight.current = false;
        setIsCheckingPlan(false);
      }
    })();
  }, [
    isAuthenticated,
    location.pathname,
    isExcludedPath,
    showPlanExpired,
    isFreePlan,
    setPlanExpiredPopup,
    clearPlanExpiredPopup,
  ]);

  // For paid plans: navigate to /plans
  const handleUpgrade = useCallback(() => {
    clearPlanExpiredPopup();
    navigate('/plans');
  }, [clearPlanExpiredPopup, navigate]);

  // ── Routes ────────────────────────────────────────────────────────────────
  const routes = createRoutes();
  const element = useRoutes(routes);

  return (
    <>
      <Suspense fallback={<Spinner />}>{element}</Suspense>

      <Tooltip
        id="global-tooltip"
        place="top"
        className="global-tooltip"
        offset={8}
        border="1px solid #cfcbcb"
      />

      {isAuthenticated && <FeedbackPopup />}
      {isAuthenticated && isChatBotEnabled && <ChatWidget />}

      {isAuthenticated && showPlanExpired && !isExcludedPath && !isCheckingPlan && (
        <PlanExpiredPopup
          isFreePlan={isFreePlan}
          canExtend={canExtend}
          extensionRequested={extensionRequested}
          onRequestExtend={requestPlanExtension}
          onUpgrade={handleUpgrade}
          onClose={clearPlanExpiredPopup}
        />
      )}

      <NewInSalesroute
        isOpen={showRelease && !isExcludedPath}
        onClose={handleReleaseClose}
        release={release}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
